import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {useDeleteSupplierOrderVariantMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError, usePermissions} from '@dms/shared';

import {composePath, noop, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function WarehouseSupplierOrderVariantsList() {
  const navigate = useNavigate();

  const [warehouseDictionaryCreate] = usePermissions({
    permissionKeys: ['warehouseDictionaryCreate'],
  });

  const [deleteSupplierOrderVariant] = useDeleteSupplierOrderVariantMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    match(actionKey)
      .with('redirectDetail', 'edit', () => {
        const singleRowId = isArray(rowId) ? rowId[0] : rowId;
        navigate(
          composePath(settingsRoutes.warehousesSupplierOrderVariantsDetail, {
            params: {id: singleRowId},
          })
        );
      })
      .with('delete', () => {
        const rowIds = isArray(rowId) ? rowId : [rowId];
        openDeleteDialog({
          text: i18n.t('entity.warehouse.labels.deleteSelectedItemsPrompt'),
          onConfirm: () => {
            deleteSupplierOrderVariant({body: {orderVariantId: rowIds}})
              .unwrap()
              .then(() =>
                showNotification.success(
                  i18n.t('entity.warehouse.notifications.supplierOrderVariantDeleted')
                )
              )
              .then(() => refreshData())
              .catch(handleApiError);
          },
        });
      })
      .otherwise(noop);
  };

  const header: SettingsTemplateHeader = {
    title: i18n.t('entity.warehouse.labels.warehouseSupplierOrderVariants'),
    actions: [
      {
        type: 'button',
        title: i18n.t('entity.warehouse.actions.newWarehouseSupplierOrderVariant'),
        onClick: () => navigate(settingsRoutes.warehousesSupplierOrderVariantsCreate),
        isDisabled: !warehouseDictionaryCreate,
        tooltip: !warehouseDictionaryCreate
          ? i18n.t('entity.warehouse.labels.missingWarehouseDictionaryCreatePermission')
          : undefined,
      },
    ],
  };

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.supplierOrderVariants')} />
      <SettingsTemplate
        header={header}
        data-testid={testIds.settings.warehousesSupplierOrderVariants('template')}
      >
        <DmsDataGrid
          gridCode="warehouse-supplier-order-variants"
          actionCallback={actionCallback}
          data-testid={testIds.settings.warehousesSupplierOrderVariants('supplier-order-variants')}
        />
      </SettingsTemplate>
    </>
  );
}
